/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import {
  ColumnGroupInterface,
  useTable,
  useExpanded,
  usePagination,
  useSortBy,
  useFlexLayout,
  useResizeColumns,
  UseExpandedRowProps,
} from 'react-table';

import { useSticky } from 'react-table-sticky';
import classNames from 'classnames';

import {
  TableDatum,
  TableColumn,
  TableOptions,
  FetchDataArgument,
} from '../types';

import Text from '../../../atoms/Text';
import EmptyTableComponent from '../EmptyTableComponent';
import Spinner from '../../../atoms/Spinner';
import TableSortIcon from '../../../atoms/TableSortIcon';
import DataTablePagination from '../../../molecules/DataTablePagination';
import styles from '../DataTable.module.scss';
import { getHiddenColumnIds } from '../utils/columnUtils';
import {
  DataTableCellProps,
  DataTableInstanceProps,
  DataTableOptions,
} from './types';

export interface Props<T extends TableDatum>
  extends React.HTMLAttributes<HTMLElement> {
  tableId: string;
  columns: Array<TableColumn<T>>;
  data: Array<TableDatum>;
  loading?: boolean;
  getCellProps?: (cell: DataTableCellProps) => Record<'className', string>;
  // Pagination options
  pagination?: boolean;
  fetchData?: (arg: FetchDataArgument) => void;
  totalCount?: number;
  pageCount?: number;
  onChangePage?: (n: number) => void;
  onChangePageSize?: (n: number) => void;
  pageSize?: number;
  pageIndex?: number;
  // Sorting options
  manualSortBy?: boolean;
  initialSortBy?: Array<{ id: string; desc?: boolean }>;
  onChangeSortBy?: (arg: Array<{ id: string; desc?: boolean }>) => void;
  // Row Expansion options
  expanded?: Record<string, boolean>;
  /** @deprecated use expanded instead */
  startExpanded?: boolean;
  // Table options
  options?: TableOptions;
  // Translation
  i18n?: {
    emptyText: string;
  };
  append?: React.ReactNode;
}

const splitKeyProp = <T extends object>(props: T) => {
  const keyedProps = props as T & { key?: React.Key };
  const { key, ...rest } = keyedProps;
  return { key, props: rest as Omit<T, 'key'> };
};

/**
 * <DataTable> component
 */
export const GroupedRowsDataTable = <T extends TableDatum>({
  tableId,
  className: tableClassName,
  columns,
  data,
  loading,
  getCellProps,
  fetchData,
  // Pagination options
  pagination = false,
  totalCount,
  pageCount: controlledPageCount,
  onChangePageSize,
  onChangePage,
  pageSize = 50,
  pageIndex = 0,
  // Sorting options
  manualSortBy = false,
  initialSortBy = [],
  onChangeSortBy,
  // Group options
  expanded = {},
  // Table options
  options = {},
  // Translation
  i18n = { emptyText: 'Nenhum dado foi encontrado' },
  append = null,
  ...htmlElementProps
}: React.PropsWithChildren<Props<T>>): React.ReactElement => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    // Pagination
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // Get the state from the instance
    state: { sortBy },
  } = useTable(
    {
      columns,
      data,
      // default column size
      defaultColumn: { minWidth: 50 },
      // Sort definitions
      manualSortBy,
      // Pass our hoisted table state
      initialState: {
        pageSize,
        pageIndex,
        expanded,
        sortBy: initialSortBy,
        hiddenColumns: getHiddenColumnIds(columns),
      },
      // prevent table state from automatically resetting when data changes
      // Ref.: https://react-table.tanstack.com/docs/faq#how-do-i-stop-my-table-state-from-automatically-resetting-when-my-data-changes
      autoResetPage: false,
      // Tell the usePagination hook that we'll handle our own data fetching
      // This means we'll also have to provide our own pageCount.
      manualPagination: true,
      pageCount: controlledPageCount,
      // User defined table options
      ...options,
    } as DataTableOptions,
    useFlexLayout,
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useSticky,
  ) as DataTableInstanceProps;

  // Store new sort state in reducer and call API to fetch new data from server
  React.useEffect(() => {
    if (!onChangeSortBy) return;
    onChangeSortBy(sortBy);
  }, [onChangeSortBy, sortBy]);

  // Check if table has any footer definition
  const hasFooter = React.useMemo(
    () =>
      columns
        .flatMap((column) =>
          (column as ColumnGroupInterface<T>).columns
            ? (column as ColumnGroupInterface<T>).columns
            : column,
        )
        .some((column) => column.Footer),
    // There is currently a bug with eslint-plugin-react-hooks v1
    // and typescript-eslint v4.0 in which the dependency array shows an
    // error for missing dependency on the generic type parameter
    // Ref.: https://github.com/facebook/react/issues/19808
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns],
  );
  const footerGroups = headerGroups.slice().reverse();

  // Filter only footerGroups with at least 1
  const filteredFooterGroups = footerGroups.filter((group) =>
    group.headers.every((column) => !!column.Footer),
  );

  // Get table className
  const tableCls = classNames('table sticky', tableClassName);

  const pageRange = React.useMemo(() => {
    const pageStart = pageIndex * pageSize;
    const possiblePageEnd = pageStart + pageSize;
    return `${pageStart + 1}-${Math.min(totalCount || 0, possiblePageEnd)}`;
  }, [pageIndex, pageSize, totalCount]);

  const paginationText = `Mostrando ${pageRange} de ${Math.max(
    totalCount || 0,
    data.length,
  )} resultados`;
  const tableProps = getTableProps();
  const tableBodyProps = getTableBodyProps();
  const { key: tableKey, props: tableDivProps } = splitKeyProp(tableProps);
  const { key: tableBodyKey, props: tableBodyDivProps } =
    splitKeyProp(tableBodyProps);

  return (
    <>
      {/* The table */}
      <div className={styles.wrapper} {...htmlElementProps}>
        <div
          key={tableKey}
          id={tableId}
          className={classNames(tableCls, styles.table)}
          role="table"
          {...tableDivProps}
        >
          <div className={styles.thead} role="rowgroup">
            {headerGroups.map((headerGroup, idxGroup) => {
              const headerGroupProps = headerGroup.getHeaderGroupProps();
              const { key: headerGroupKey, props: headerGroupDivProps } =
                splitKeyProp(headerGroupProps);

              return (
                <div
                  key={headerGroupKey}
                  {...headerGroupDivProps}
                  className={styles.tr}
                  role="row"
                >
                  {headerGroup.headers.map((column, idx) => {
                    const columnHeaderProps = column.getHeaderProps(
                      column.getSortByToggleProps({
                        title: column.canSort ? 'Clique para ordenar' : '',
                      }),
                    );
                    const {
                      key: columnHeaderKey,
                      props: columnHeaderDivProps,
                    } = splitKeyProp(columnHeaderProps);

                    return (
                      <div
                        key={columnHeaderKey}
                        {...columnHeaderDivProps}
                        className={classNames(
                          styles.th,
                          column.headerClassName,
                          {
                            [styles['first-of-row']]:
                              idx === 0 && idxGroup === 0,
                          },
                        )}
                        role="columnheader"
                        data-sortable={column.canSort}
                      >
                        <Text
                          as="span"
                          size="sm2"
                          weight="bold"
                          className={classNames(column.headerClassName)}
                        >
                          {column.render('Header')}
                        </Text>

                        <span className={styles.sorter}>
                          {column.canSort && (
                            <TableSortIcon
                              isSorted={column.isSorted}
                              direction={column.isSortedDesc ? 'desc' : 'asc'}
                              disabled={!column.canSort}
                            />
                          )}
                        </span>

                        {column.canResize !== false &&
                          (() => {
                            const resizerProps = column.getResizerProps();
                            const { key: resizerKey, props: resizerDivProps } =
                              splitKeyProp(resizerProps);

                            return (
                              <div
                                key={resizerKey}
                                {...resizerDivProps}
                                className={`${styles.resizer} ${
                                  column.isResizing ? styles.isResizing : ''
                                }`}
                              />
                            );
                          })()}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Table body */}
          <div
            key={tableBodyKey}
            {...tableBodyDivProps}
            className={styles.tbody}
            role="rowgroup"
          >
            {/* Empty table design */}
            {loading && (
              <div className={classNames(styles.tr, styles.loading)} role="row">
                <Spinner className={styles.spinner} />
              </div>
            )}

            {page.length === 0 && !loading && (
              <div className={classNames(styles.tr, styles.empty)} role="row">
                <EmptyTableComponent mainText={i18n.emptyText} />
              </div>
            )}

            {/* Table rows */}
            {!loading &&
              page.map((row) => {
                prepareRow(row);

                const expandableRow =
                  row as unknown as UseExpandedRowProps<TableDatum>;
                const rowProps = row.getRowProps();
                const { key: rowKey, props: rowDivProps } =
                  splitKeyProp(rowProps);

                return (
                  <div
                    key={rowKey}
                    {...rowDivProps}
                    className={classNames(styles.tr, {
                      [styles['group-row']]: expandableRow.canExpand,
                    })}
                    role="row"
                  >
                    {row.cells.map((cell: DataTableCellProps, idx) => {
                      const parentHeader =
                        cell.column.parent?.columns &&
                        cell.column?.parent?.columns[
                          (cell.column?.parent?.columns.length || 1) - 1
                        ].Header;
                      const cellProps = cell.getCellProps();
                      const { key: cellKey, props: cellDivProps } =
                        splitKeyProp(cellProps);

                      return (
                        <div
                          key={cellKey}
                          {...cellDivProps}
                          className={classNames(
                            styles.td,
                            cell.column.className,
                            getCellProps && getCellProps(cell).className,
                            {
                              [styles['last-of-group']]:
                                cell.column.Header === parentHeader &&
                                !(
                                  row as unknown as UseExpandedRowProps<TableDatum>
                                ).canExpand,
                              [styles['first-of-row']]: idx === 0,
                            },
                          )}
                        >
                          <span title={cell.value} className={styles.cell}>
                            {cell.render('Cell')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </div>

          {/* Table Footer */}
          {hasFooter && (
            <div className={styles.tfoot} role="rowgroup">
              {filteredFooterGroups.map((footerGroup) => {
                const footerGroupProps = footerGroup.getHeaderGroupProps();
                const { key: footerGroupKey, props: footerGroupDivProps } =
                  splitKeyProp(footerGroupProps);

                return (
                  <div
                    key={footerGroupKey}
                    {...footerGroupDivProps}
                    className={styles.tr}
                    role="row"
                  >
                    {footerGroup.headers.map((column) => {
                      const footerColumnProps = column.getHeaderProps();
                      const {
                        key: footerColumnKey,
                        props: footerColumnDivProps,
                      } = splitKeyProp(footerColumnProps);

                      return (
                        <div
                          key={footerColumnKey}
                          {...footerColumnDivProps}
                          className={styles.td}
                          role="cell"
                        >
                          <Text as="span" size="sm2" weight="bold">
                            {column.render('Footer')}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {append}
        </div>
      </div>

      {/* Table Pagination */}
      {pagination && (
        <DataTablePagination
          label={paginationText}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageSize={pageSize}
          currentPage={pageIndex}
          pageCount={pageCount}
          gotoPage={onChangePage || gotoPage}
          nextPage={nextPage}
          previousPage={previousPage}
          setPageSize={onChangePageSize || setPageSize}
        />
      )}
    </>
  );
};

export default GroupedRowsDataTable;
